import { CalendarDay } from "@datepicker-react/hooks"

/** Returns which week of the month a given date belongs to.
 * Week counting starts from the first week that contains the first day of the month.
 * Example:- Jan 1, 2026 is Thursday
 * - Jan 1–3 → Week 1
 * - Jan 4–10 → Week 2
 * - Jan 11–17 → Week 3
 */
const getWeekOfMonth = (date: Date, firstDayOfWeek = 0) => {
  const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1) // First day of the current month (e.g., Jan 1, 2026)
  const offset = (startOfMonth.getDay() - firstDayOfWeek + 7) % 7 // Calculate how many days the first week is shifted based on which day the month starts on

  // Calculate week number:
  // (date + offset) tells us how many days have passed including padding
  return Math.floor((date.getDate() + offset - 1) / 7) + 1
}

/** Splits an array into smaller arrays of equal size. Used here to divide calendar days into weekly rows.
 * Example:
 * Input  : [1,2,3,4,5,6,7,8,9]
 * Output : [[1,2,3,4,5,6,7], [8,9]]
 */
const chunkByWeeks = <T>(arr: T[], size = 7): T[][] => {
  const chunks: T[][] = []

  // Loop through array and create chunks of given size
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size))
  }
  return chunks
}

/** Returns only the calendar days that should be visible based on the current date and display rules.
 * Rules:
 * 1. If current date is in Week 1 or Week 2 → show full month
 * 2. If current date is after Week 2 → show calendar starting from one week before the current week
 */
export const getVisibleCalendarDays = (
  days: CalendarDay[],
  year: number,
  month: number,
  firstDayOfWeek = 0,
): CalendarDay[] => {
  const today = new Date() // Get today's date
  const isCurrentMonth =
    today.getFullYear() === year && today.getMonth() === month // Check if the calendar month is the current month
  const weekOfMonth = isCurrentMonth ? getWeekOfMonth(today, firstDayOfWeek) : 1 // Get week number only for current month
  const weeks = chunkByWeeks(days, 7) // Split all calendar days into weekly chunks

  // Decide from which week the calendar should start
  // Week 1 or 2 → show full month , Week 3+ → show from (currentWeek - 1)
  const startWeekIndex = weekOfMonth <= 2 ? 0 : Math.max(weekOfMonth - 2, 0)
  return weeks.slice(startWeekIndex).flat() // Get only required weeks and flatten them into days
}
